import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ProductServices } from "../../Firebase/Services/product.services";
import { TProductDetail } from "../../types";
import { HeroBg } from "../../components/Assets";
import { motion } from "framer-motion";
import { formatCurrency } from "../../utils/format-number";
import { SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import { Swiper } from "swiper/react";
import { Helmet, HelmetProvider } from "react-helmet-async";

const ProductDetail = () => {
  const { code } = useParams<{ code: string }>();
  const [product, setProduct] = useState<TProductDetail | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchProductDetail = async () => {
      if (!code) return;
      setLoading(true);
      const res = await ProductServices.getProductDetail(code);
      setProduct(res);
      setLoading(false);
    };

    fetchProductDetail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const swiperSlideItem = product?.imageBack
    ? [product.image, product.imageBack]
    : [product?.image];

  const productName = product?.name ?? "...";
  const productCode = product?.code ?? "...";

  if (loading) {
    return (
      <div className="grid grid-cols-12 pt-12 lg:px-20">
        <div
          className="relative flex justify-center col-span-12 mb-6 bg-no-repeat bg-contain lg:mb-0 lg:col-span-6"
          style={{ backgroundImage: `url(${HeroBg})` }}
        >
          <div className="w-[80%] lg:h-[420px] py-5">
            <div className="h-full bg-gray-300 aspect-square rounded-xl animate-pulse opacity-8"></div>
          </div>
        </div>
        <div className="col-span-12 lg:col-span-6 lg:pl-5">
          <div className="mb-3 w-[90%] lg:w-96 h-8 lg:h-10 bg-gray-300 rounded-xl animate-pulse opacity-8"></div>
          <div className="h-8 mb-3 bg-gray-300 w-36 lg:h-10 rounded-xl animate-pulse opacity-8"></div>
          <div className="px-2 py-1 mb-6 bg-gray-300 rounded-md w-fit">
            <div className="w-20 h-6 bg-gray-300"></div>
          </div>
          <div className="h-8 mb-2 bg-gray-300 lg:mb-4 w-52 rounded-xl animate-pulse opacity-8"></div>
          <ul>
            <li className="flex mb-5">
              <div className="mb-2 lg:mb-4 w-[90%] lg:w-96 h-7 bg-gray-300 rounded-xl animate-pulse opacity-8"></div>
            </li>
            <li className="flex mb-5">
              <div className="mb-2 lg:mb-4 w-[90%] lg:w-96 h-7 bg-gray-300 rounded-xl animate-pulse opacity-8"></div>
            </li>
            <li className="flex mb-5">
              <div className="mb-2 lg:mb-4 w-[90%] lg:w-96 h-7 bg-gray-300 rounded-xl animate-pulse opacity-8"></div>
            </li>
          </ul>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <Helmet>
        <title>
          {`${productName} - ${productCode}` || "Chi tiết sản phẩm"}
        </title>
        <meta
          name="description"
          content={
            `${productCode} - ${product?.ingredients}` ||
            "Mô tả chi tiết sản phẩm"
          }
        />
        <meta
          name="keywords"
          content={`sản phẩm, ${productName}, giá rẻ, chất lượng. Mã vạch ${productCode}`}
        />
        <meta
          property="og:title"
          content={productName || "Chi tiết sản phẩm"}
        />
        <meta property="og:description" content={product?.ingredients} />
        <meta property="og:code" content={productCode} />
        <meta property="og:url" content={window.location.href} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            name: productName,
            description: product?.ingredients,
            sku: productCode,
            mpn: productCode,
            brand: {
              "@type": "Brand",
              name: "GiaiGiai",
            },
            offers: {
              "@type": "Offer",
              url: window.location.href,
              priceCurrency: "VND",
              price: product?.price ?? 0,
              itemCondition: "https://schema.org/NewCondition",
              availability: "https://schema.org/InStock",
              hasMerchantReturnPolicy: undefined,
              shippingDetails: undefined,
              priceValidUntil: "2028-12-31",
            },
            aggregateRating: {
              "@type": "AggregateRating",
              ratingValue: 5,
              reviewCount: 10,
            },
            review: {
              "@type": "Review",
              author: {
                "@type": "Person",
                name: "Người dùng ẩn danh",
              },
              reviewRating: {
                "@type": "Rating",
                ratingValue: 5,
              },
            },
            image: "https://giaigiai.com/favicon.png",
          })}
        </script>
      </Helmet>
      <div className="grid grid-cols-12 pt-12 lg:gap-10 xl:px-40">
        <div
          className="relative flex items-center justify-center col-span-12 mb-6 bg-no-repeat bg-contain lg:mb-0 lg:col-span-5"
          style={{ backgroundImage: `url(${HeroBg})` }}
        >
          <Swiper
            navigation={true}
            modules={[Navigation]}
            className="mySwiper w-[90%]"
          >
            {swiperSlideItem.map((item, index) => {
              return (
                <SwiperSlide key={`productImage ${index}`}>
                  <motion.img
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 1.1 }}
                    src={item}
                    alt={productCode}
                    className="object-contain w-full"
                  />
                </SwiperSlide>
              );
            })}
          </Swiper>
        </div>

        {/* Product Details */}
        <div className="col-span-12 m-auto lg:col-span-6">
          <h1 className="mb-3 text-3xl font-semibold">{productName}</h1>
          <h3 className="mb-3 text-3xl font-medium text-orange-600">
            {formatCurrency(product?.price ?? 0)}
          </h3>
          <div className="px-2 py-1 mb-6 bg-gray-300 rounded-md w-fit">
            <h2 className="text-textColor">#{productCode}</h2>
          </div>
          <p className="mb-2 text-xl text-gray-500 lg:mb-4">Mô tả sản phẩm:</p>
          <ul>
            <li className="flex flex-col gap-2 mb-5 lg:flex-row lg:gap-0">
              <p className="text-base lg:text-lg min-w-[190px] lg:min-w-[220px]">
                Thành phần
              </p>{" "}
              <span className="text-base text-gray-500 lg:text-lg">
                {product?.ingredients}
              </span>
            </li>
            <li className="flex mb-5">
              <p className="text-base lg:text-lg min-w-[190px] lg:min-w-[220px]">
                Nơi sản xuất
              </p>{" "}
              <span className="text-base text-gray-500 lg:text-lg">
                {product?.address}
              </span>
            </li>
            <li className="flex mb-5">
              <p className="text-base lg:text-lg min-w-[190px] lg:min-w-[220px]">
                Số điện thoại liên hệ{" "}
              </p>
              <a
                href="tel:0369980580"
                className="text-base text-gray-500 lg:text-lg"
              >
                0369 980 580
              </a>
            </li>
          </ul>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default ProductDetail;
